<template>
  <LoginLayout class="confirm-plan">
    <h1>{{ hasPreselection ? "Confirm your plan" : "Choose your plan" }}</h1>
    <h2>Pick the plan that fits your needs.</h2>
    <div class="toolbar">
      <BaseButtonGroup>
        <template #firstButton>
          <BaseButton
            kind="main"
            type="secondary"
            text="Monthly"
            :state="interval === 'month' ? 'active' : 'default'"
            @click="interval = 'month'"
          />
        </template>
        <template #lastButton>
          <BaseButton
            kind="main"
            type="secondary"
            text="Yearly"
            :state="interval === 'year' ? 'active' : 'default'"
            @click="interval = 'year'"
          />
        </template>
      </BaseButtonGroup>
    </div>
    <div class="wrapper-plan">
      <div v-if="isRefreshing" class="plans">
        <PlanSkeleton v-for="entry in entries" :key="entry.product.id" />
      </div>
      <Suspense v-else>
        <div class="plans">
          <div
            v-for="entry in entries"
            :key="entry.product.id"
            class="plan-option"
            @click="selectedProductId = entry.product.id"
          >
            <Plan
              mode="expanded"
              layout="vertical"
              :product="entry.product"
              :price="entry.price"
              :subscription="subscription"
              :org-id="orgId"
              :preselected="entry.product.id === preselectedProductId"
              :selected="entry.product.id === selectedProductId"
              :show-action="false"
            />
          </div>
        </div>
        <template #fallback>
          <div class="plans">
            <PlanSkeleton v-for="entry in entries" :key="entry.product.id" />
          </div>
        </template>
      </Suspense>
    </div>
    <div class="buttons">
      <BaseButton
        v-if="isSubscribed"
        kind="important"
        type="primary"
        text="Launching…"
        state="loading"
      />
      <template v-else>
        <BaseButton
          kind="important"
          type="primary"
          :text="upgradeText"
          :state="
            isUpgrading
              ? 'loading'
              : isRefreshing || !hasSelection
                ? 'disabled'
                : 'default'
          "
          @click="upgrade"
        />
        <BaseButton
          v-if="freemium"
          kind="important"
          type="tertiary"
          text="Skip →"
          :state="isNextLoading ? 'loading' : 'default'"
          @click="$emit('nextStep')"
        />
      </template>
    </div>
    <div class="confetti-container">
      <ConfettiExplosion
        v-if="boom"
        :stage-height="800"
        :duration="5000"
        :particle-size="8"
      />
    </div>
  </LoginLayout>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  watch,
} from "vue";
import ConfettiExplosion from "vue-confetti-explosion";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseButtonGroup from "@/components/base/BaseButtonGroup.vue";
import * as inject from "@/components/inject";
import LoginLayout from "@/components/login/LoginLayout.vue";
import Plan from "@/components/settings/Plan.vue";
import PlanSkeleton from "@/components/settings/PlanSkeleton.vue";
import { metrics } from "@/metrics";
import type {
  Billing,
  BillingInterval,
  BillingPrice,
  BillingProduct,
} from "@/utils/billing";
import { FORCED_FREEMIUM } from "@/utils/billing";
import { openExternalUrl } from "@/utils/open-external-url";

/** The onboarding path taken, decided at the use-case step. */
export type ConfirmPlanUseCase = "individual" | "team";

export type ConfirmPlanEntry = {
  product: BillingProduct;
  price: BillingPrice;
};

/**
 * Resolve the plans the confirm-plan onboarding step displays for the path
 * the user took: per-user (flat) plans on the individual path, organization
 * (seat-based) plans on the team path — the same classification
 * Subscription.vue applies to personal vs team organizations. Only premium
 * products with a visible, clearly priced offer for the preselected
 * interval qualify: custom ("Contact us") plans like Enterprise are not
 * shown during onboarding (the subscription settings still offer them),
 * and the Free plan is covered by the skip button.
 *
 * All the valid plans are displayed, cheapest first — a plan preselected on
 * the website's pricing page does not narrow the list; the component only
 * highlights it as the initial selection.
 *
 * Returns an empty list when nothing can be displayed (e.g. the pricing
 * catalog has not loaded) — in which case the step is skipped entirely.
 */
export function resolveConfirmPlanEntries(
  billing: Pick<Billing, "products"> | undefined,
  useCase: ConfirmPlanUseCase,
  interval: BillingInterval
): ConfirmPlanEntry[] {
  const entries: ConfirmPlanEntry[] = [];
  for (const product of billing?.products?.value ?? []) {
    if (product.plan === "free") continue;
    const price = product.prices.find(
      (p) => p.interval === interval && p.visible
    );
    if (!price) continue;
    // No clear price to display or subscribe to during onboarding.
    if (price.model === "custom") continue;
    const isTeamPlan = price.model === "seat";
    if (isTeamPlan !== (useCase === "team")) continue;
    entries.push({ product, price });
  }
  entries.sort((a, b) => a.price.amount - b.price.amount);
  return entries;
}

export default defineComponent({
  components: {
    BaseButton,
    BaseButtonGroup,
    ConfettiExplosion,
    LoginLayout,
    Plan,
    PlanSkeleton,
  },
  props: {
    /**
     * The organization the displayed plans apply to: the personal
     * organization on the individual path, the created team organization on
     * the team path.
     */
    orgId: {
      type: String,
      required: true,
    },
    /** The onboarding path taken, deciding which plans are valid. */
    useCase: {
      type: String as PropType<ConfirmPlanUseCase>,
      required: true,
    },
    /** Plan name chosen on the /pricing page the user signed up from. */
    preselectedPlan: {
      type: String,
      required: false,
    },
    preselectedInterval: {
      type: String as PropType<BillingInterval>,
      required: false,
    },
    isNextLoading: { type: Boolean, required: false, default: false },
  },
  emits: {
    nextStep: () => true,
  },
  setup(props, { emit }) {
    const billing = inject.billing();
    const controller = inject.controller();
    const logger = inject.logger("ConfirmPlan");

    // Checkout happens in the browser, so refresh the subscription whenever
    // the user comes back to the app (same workaround as Subscription.vue).
    // No refresh on first open: the user just arrived from onboarding and
    // cannot have an in-flight plan change yet.
    {
      const handleFocus = () => {
        void billing.value?.refreshSubscriptions();
      };
      onMounted(() => window.addEventListener("focus", handleFocus));
      onBeforeUnmount(() => window.removeEventListener("focus", handleFocus));
    }

    // The displayed billing interval, initialized from the pricing page's
    // preselection and switchable with the Monthly/Yearly toggle.
    const interval = ref<BillingInterval>(props.preselectedInterval ?? "year");

    const entries = computed(() =>
      resolveConfirmPlanEntries(billing.value, props.useCase, interval.value)
    );

    // The displayed plan matching the one preselected on the website's
    // pricing page, when it is among them. The plan names are lowercase in
    // the URL (e.g. plan=professional) while product names are capitalized.
    const preselectedProductId = computed(() => {
      const name = props.preselectedPlan?.toLowerCase();
      if (name === undefined) return undefined;
      return entries.value.find(
        (entry) => entry.product.name.toLowerCase() === name
      )?.product.id;
    });
    const hasPreselection = computed(
      () => preselectedProductId.value !== undefined
    );

    // The card the user selected (by clicking it), acted on by the Upgrade
    // button. Starts on the preselected plan when it is displayed; otherwise
    // nothing is selected (and Upgrade is disabled) until the user picks a
    // card. Reset whenever the selection no longer exists (e.g. the pricing
    // catalog finishes loading).
    const selectedProductId = ref<string>();
    watch(
      entries,
      (list) => {
        if (!list.some((entry) => entry.product.id === selectedProductId.value))
          selectedProductId.value = preselectedProductId.value;
      },
      { immediate: true }
    );
    const selectedEntry = computed(() =>
      entries.value.find(
        (entry) => entry.product.id === selectedProductId.value
      )
    );
    const hasSelection = computed(() => selectedEntry.value !== undefined);

    const subscription = computed(
      () => billing.value?.getSubscription(props.orgId).value ?? null
    );

    const isRefreshing = computed(
      () => billing.value?.isRefreshing.value ?? false
    );

    // The user actually subscribed to one of the displayed plans (a trial
    // does not count): celebrate and launch the app automatically.
    const isSubscribed = computed(() => {
      const subscribedProduct = subscription.value?.product;
      return (
        !!subscribedProduct &&
        subscription.value?.status !== "trialing" &&
        entries.value.some((entry) => entry.product.id === subscribedProduct.id)
      );
    });

    // Give the confetti a moment before the app swaps the screen; the
    // explosion keeps playing while onboarding completion runs.
    const CONFETTI_LAUNCH_DELAY_MS = 1500;
    const boom = ref(false);
    let launchTimer: number | undefined;
    onBeforeUnmount(() => window.clearTimeout(launchTimer));
    watch(
      isSubscribed,
      (subscribed) => {
        if (!subscribed || boom.value) return;
        boom.value = true;
        launchTimer = window.setTimeout(
          () => emit("nextStep"),
          CONFETTI_LAUNCH_DELAY_MS
        );
      },
      { immediate: true }
    );

    // Plans not available for self-serve checkout book a call instead, like
    // the corresponding card action in Subscription.vue. (Custom "Contact
    // us" plans are not displayed here at all.)
    const isBookDemo = computed(() => {
      const price = selectedEntry.value?.price;
      return !!price && !price.available;
    });
    const upgradeText = computed(() =>
      isBookDemo.value ? "Book demo" : "Upgrade"
    );

    const isUpgrading = ref(false);
    const upgrade = async () => {
      const entry = selectedEntry.value;
      logger.info("upgrade clicked", {
        orgId: props.orgId,
        product: entry?.product.id,
        productName: entry?.product.name,
        price: entry?.price.id,
        isBookDemo: isBookDemo.value,
        isUpgrading: isUpgrading.value,
        hasBilling: billing.value !== undefined,
      });
      if (!entry || isUpgrading.value) return;
      if (isBookDemo.value) {
        openExternalUrl("https://routine.co/contact", controller);
        return;
      }
      isUpgrading.value = true;
      try {
        await billing.value?.switchSubscription(props.orgId, entry.price.id);
      } finally {
        isUpgrading.value = false;
      }
    };

    metrics.segment.page("Onboarding", { step: "plan" });

    return {
      boom,
      entries,
      freemium: FORCED_FREEMIUM,
      hasPreselection,
      hasSelection,
      interval,
      isRefreshing,
      isSubscribed,
      isUpgrading,
      preselectedProductId,
      selectedProductId,
      subscription,
      upgrade,
      upgradeText,
    };
  },
});
</script>

<style scoped lang="scss">
// LoginLayout offsets its content upwards to compensate for the wave
// graphic of the login screens; this step has no wave, so recenter.
.confirm-plan :deep(.login-content) {
  margin-top: 0;
}

.toolbar {
  display: flex;
  justify-content: center;
  margin-top: 0px;
}

.wrapper-plan {
  display: flex;
  justify-content: center;
  margin: 16px 0 24px 0;
}

.plans {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
  gap: 16px;
  padding: 0 24px;
  // LoginLayout centers text; the cards are start-aligned like the
  // pricing page.
  text-align: start;

  .plan-option {
    display: flex;
    align-items: stretch;
  }

  :deep(.plan) {
    width: 260px;
  }
}

.buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.confetti-container {
  position: fixed;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  z-index: $z-index-critical;
}
</style>
